import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaSearch, 
  FaFilter, 
  FaFlag, 
  FaTrash, 
  FaCheck, 
  FaEye, 
  FaExclamationTriangle, 
  FaUser, 
  FaCalendarAlt,
  FaTimes,
  FaExternalLinkAlt
} from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import Pagination from '../Common/Pagination';
import './SpamReports.css';

const SpamReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  // Modal State for viewing full post details
  const [selectedPost, setSelectedPost] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      const res = await api.get(ENDPOINTS.COMMUNITY.REPORTS, { params });
      const data = res.data?.reports || res.reports || [];
      const total = res.data?.total || res.total || 0;
      
      setReports(data);
      setPagination(prev => ({ ...prev, total }));
    } catch (error) {
      console.error('Error fetching spam reports:', error);
      toast.error('Failed to load spam reports');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Filtered reports locally for search & role filters (additional client-side filter)
  const filteredReports = reports.filter(report => {
    // 1. Search Query Filter
    const query = searchQuery.toLowerCase();
    const reporterName = `${report.reporter?.firstName || ''} ${report.reporter?.lastName || ''}`.toLowerCase();
    const reason = (report.reason || '').toLowerCase();
    const postContent = (report.post?.content || '').toLowerCase();
    const authorName = `${report.post?.author?.firstName || ''} ${report.post?.author?.lastName || ''}`.toLowerCase();

    const matchesSearch = 
      reporterName.includes(query) ||
      reason.includes(query) ||
      postContent.includes(query) ||
      authorName.includes(query);

    // 2. Role Filter
    const matchesRole = roleFilter === 'all' || report.reporter?.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const handleDismissReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to dismiss this report? The post will remain on the platform.')) {
      return;
    }
    try {
      await api.delete(ENDPOINTS.COMMUNITY.DELETE_REPORT(reportId));
      toast.success('Report dismissed successfully');
      fetchReports();
    } catch (error) {
      console.error('Error dismissing report:', error);
      toast.error('Failed to dismiss report');
    }
  };

  const handleDeletePost = async (postId, reportId) => {
    if (!window.confirm('Are you sure you want to DELETE this post? This action is permanent and will remove the post and all its reports.')) {
      return;
    }
    try {
      await api.delete(ENDPOINTS.COMMUNITY.DELETE_POST(postId));
      toast.success('Post deleted successfully');
      setModalOpen(false);
      fetchReports();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, page: pageNumber }));
  };

  const openPostModal = (post, reportId) => {
    setSelectedPost({ ...post, reportId });
    setModalOpen(true);
  };

  return (
    <div className="spam-reports-page">
      <div className="page-header">
        <div>
          <h1>Community Spam Reports</h1>
          <p className="page-subtitle">Manage flagged community posts reported by Customers and Providers</p>
        </div>
      </div>

      {/* Stats Summary Cards */}
      <div className="reports-stats">
        <div className="stat-mini-card danger">
          <div className="stat-mini-icon">
            <FaFlag />
          </div>
          <div className="stat-mini-content">
            <h3>{pagination.total}</h3>
            <p>Active Spam Reports</p>
          </div>
        </div>
        <div className="stat-mini-card warning">
          <div className="stat-mini-icon">
            <FaExclamationTriangle />
          </div>
          <div className="stat-mini-content">
            <h3>{reports.filter(r => r.post).length}</h3>
            <p>Reported Posts In List</p>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="content-card filters-card">
        <div className="filters-row">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search reports by user, post content, or reason..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select 
              value={roleFilter} 
              onChange={(e) => setRoleFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Reporter Roles</option>
              <option value="customer">Customers (Parents)</option>
              <option value="provider">Providers (Caregivers)</option>
            </select>
          </div>
        </div>
        
        <div className="filters-summary">
          <div>
            Showing <strong>{filteredReports.length}</strong> matching reports
          </div>
        </div>
      </div>

      {/* Reports Table/List */}
      <div className="content-card">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading spam reports...</p>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🛡️</div>
            <h3>No spam reports found</h3>
            <p>The community feed is clean and no posts are currently flagged.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Reporter</th>
                  <th>Reason</th>
                  <th>Reported Post</th>
                  <th>Post Author</th>
                  <th>Date Flagged</th>
                  {/* <th className="actions-header">Actions</th> */}
                </tr>
              </thead>
              <tbody>
                {filteredReports.map((report) => (
                  <tr key={report.id}>
                    <td>
                      <div className="user-profile-info">
                        <span className={`role-badge ${report.reporter.role}`}>
                          {report.reporter.role === 'customer' ? 'Customer' : 'Provider'}
                        </span>
                        <div className="user-details">
                          <p className="user-name">{report.reporter.firstName} {report.reporter.lastName}</p>
                          <p className="user-email">{report.reporter.email}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="report-reason-cell">
                        <span className="reason-text">{report.reason || 'Spam'}</span>
                      </div>
                    </td>
                    <td>
                      {report.post ? (
                        <div className="post-snippet-cell">
                          <p className="post-snippet">
                            {report.post.content.length > 80 
                              ? `${report.post.content.substring(0, 80)}...` 
                              : report.post.content
                            }
                          </p>
                          {report.post.mediaUrls && report.post.mediaUrls.length > 0 && (
                            <span className="image-badge">📸 {report.post.mediaUrls.length} image(s)</span>
                          )}
                        </div>
                      ) : (
                        <span className="deleted-placeholder">Post has been deleted</span>
                      )}
                    </td>
                    <td>
                      {report.post ? (
                        <div className="author-cell">
                          <p className="author-name">{report.post.author.firstName} {report.post.author.lastName}</p>
                          <span className={`role-tag ${report.post.author.role}`}>
                            {report.post.author.role === 'customer' ? 'Parent' : 'Caregiver'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <div className="date-cell">
                        <FaCalendarAlt className="date-icon" />
                        <span>{new Date(report.createdAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</span>
                      </div>
                    </td>
                    {/* <td>
                      <div className="actions-cell">
                        {report.post && (
                          <button 
                            className="btn-action view" 
                            title="View Full Post"
                            onClick={() => openPostModal(report.post, report.id)}
                          >
                            <FaEye /> View
                          </button>
                        )}
                        <button 
                          className="btn-action dismiss" 
                          title="Dismiss Report"
                          onClick={() => handleDismissReport(report.id)}
                        >
                          <FaCheck /> Dismiss
                        </button>
                        {report.post && (
                          <button 
                            className="btn-action delete" 
                            title="Delete Post"
                            onClick={() => handleDeletePost(report.post.id, report.id)}
                          >
                            <FaTrash /> Delete Post
                          </button>
                        )}
                      </div>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.total > pagination.limit && (
          <Pagination
            currentPage={pagination.page}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {/* Post Details Modal */}
      {modalOpen && selectedPost && (
        <div className="report-modal-overlay">
          <div className="report-modal-content">
            <div className="modal-header">
              <h3>
                <FaExclamationTriangle className="modal-warn-icon" />
                Review Reported Post (ID: {selectedPost.id})
              </h3>
              <button className="close-btn" onClick={() => setModalOpen(false)}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="modal-section author-section">
                <h4>Post Author</h4>
                <div className="author-card">
                  <div className="author-avatar">
                    <FaUser />
                  </div>
                  <div>
                    <p className="author-name">{selectedPost.author.firstName} {selectedPost.author.lastName}</p>
                    <p className="author-meta-info">Role: <span className="capitalize">{selectedPost.author.role === 'customer' ? 'Parent' : 'Caregiver'}</span> • Created At: {new Date(selectedPost.createdAt).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="modal-section content-section">
                <h4>Post Content</h4>
                <div className="post-bubble">
                  {selectedPost.title && <h5 className="post-bubble-title">{selectedPost.title}</h5>}
                  <p className="post-bubble-content">{selectedPost.content}</p>
                </div>
              </div>

              {selectedPost.mediaUrls && selectedPost.mediaUrls.length > 0 && (
                <div className="modal-section media-section">
                  <h4>Attached Images ({selectedPost.mediaUrls.length})</h4>
                  <div className="modal-media-grid">
                    {selectedPost.mediaUrls.map((url, i) => (
                      <div key={i} className="media-wrapper">
                        <img src={url} alt={`Post Media ${i}`} className="modal-media-img" />
                        <a href={url} target="_blank" rel="noopener noreferrer" className="media-link-btn" title="View Full Image">
                          <FaExternalLinkAlt /> Open Original
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="btn btn-success" onClick={() => handleDismissReport(selectedPost.reportId)}>
                <FaCheck /> Dismiss Report
              </button>
              <button className="btn btn-danger" onClick={() => handleDeletePost(selectedPost.id, selectedPost.reportId)}>
                <FaTrash /> Delete Post & Reports
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpamReports;
